import { GOOGLE_KEY } from "../constants/Keys";

export const GOOGLE_MAPS_API_KEY = GOOGLE_KEY || "AIzaSyBxZMdYKx3vIUwVh4FvIM2rALgtExmW8hk";

let googleMapsLoaded = false;
let loadPromise = null;

/**
 * Loads the Google Maps JavaScript API with places library once (Singleton).
 */
export function loadGoogleMaps() {
  if (googleMapsLoaded || (typeof window !== "undefined" && window.google && window.google.maps)) {
    googleMapsLoaded = true;
    return Promise.resolve();
  }
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    if (typeof window !== "undefined" && window.google && window.google.maps) {
      googleMapsLoaded = true;
      resolve();
      return;
    }

    const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    if (existingScript) {
      existingScript.addEventListener("load", () => {
        googleMapsLoaded = true;
        resolve();
      });
      existingScript.addEventListener("error", () => reject(new Error("Google Maps failed to load")));
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      googleMapsLoaded = true;
      resolve();
    };
    script.onerror = () => reject(new Error("Google Maps failed to load"));
    document.head.appendChild(script);
  });

  return loadPromise;
}

/**
 * Common ISO country code mapping
 */
export const COUNTRY_MAP = {
  IN: "IN", US: "US", GB: "GB", CA: "CA", AU: "AU", DE: "DE", FR: "FR", IT: "IT",
  ES: "ES", JP: "JP", CN: "CN", BR: "BR", MX: "MX", ZA: "ZA", SG: "SG", AE: "AE",
  NZ: "NZ", NL: "NL", SE: "SE", CH: "CH", KR: "KR", TH: "TH", MY: "MY", PH: "PH",
  ID: "ID", VN: "VN", PK: "PK", BD: "BD", LK: "LK", NG: "NG", KE: "KE", EG: "EG",
  IL: "IL", SA: "SA", TR: "TR", RU: "RU", UA: "UA", PL: "PL", CZ: "CZ", GR: "GR",
  PT: "PT", BE: "BE", AT: "AT", DK: "DK", FI: "FI", NO: "NO", IE: "IE", HK: "HK",
  TW: "TW", AR: "AR", CL: "CL", CO: "CO", PE: "PE",
};

/**
 * Extracts 2-letter country code from Google Geocoder address components
 */
export function getCountryCodeFromAddress(addressComponents) {
  if (!addressComponents || !Array.isArray(addressComponents)) return null;
  for (const component of addressComponents) {
    if (component.types.includes("country")) {
      const code = component.short_name;
      return COUNTRY_MAP[code] || code;
    }
  }
  return null;
}

/**
 * Extracts city and country from Google Geocoder address components
 */
export function extractCityCountry(addressComponents) {
  let city = "";
  let country = "";
  if (!addressComponents || !Array.isArray(addressComponents)) return { city, country };

  for (const component of addressComponents) {
    if (
      component.types.includes("locality") ||
      (!city && component.types.includes("sublocality")) ||
      (!city && component.types.includes("administrative_area_level_2"))
    ) {
      city = component.long_name;
    }
    if (component.types.includes("country")) {
      country = component.long_name;
    }
  }
  return { city, country };
}

/**
 * Builds standard Google Maps custom teal marker icon
 */
export function buildMarkerIcon(google) {
  if (!google || !google.maps) return undefined;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="60" height="72" viewBox="0 0 60 72">
    <circle cx="30" cy="33" r="27" fill="rgba(45,191,207,0.13)" stroke="rgba(45,191,207,0.32)" stroke-width="1.5"/>
    <circle cx="30" cy="33" r="17" fill="rgba(45,191,207,0.18)" stroke="rgba(45,191,207,0.45)" stroke-width="1.5"/>
    <path d="M30 9 C21 9 14 16 14 25 C14 37 30 55 30 55 C30 55 46 37 46 25 C46 16 39 9 30 9 Z" fill="#2dbfcf" stroke="white" stroke-width="2.2"/>
    <circle cx="30" cy="25" r="7.5" fill="white" opacity="0.92"/>
    <circle cx="30" cy="25" r="3.8" fill="#2dbfcf"/>
  </svg>`;
  return {
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
    scaledSize: new google.maps.Size(60, 72),
    anchor: new google.maps.Point(30, 55),
  };
}

/**
 * Reverse geocodes coordinates { lat, lng } using Google Geocoder
 */
export function reverseGeocodeCoordinates(pos, callback) {
  if (!pos || typeof window === "undefined" || !window.google || !window.google.maps) {
    return;
  }
  const geocoder = new window.google.maps.Geocoder();
  geocoder.geocode({ location: pos }, (results, status) => {
    if (status === "OK" && results && results[0]) {
      const { city, country } = extractCityCountry(results[0].address_components);
      const displayText = city && country ? `${city}, ${country}` : results[0].formatted_address;
      const countryCode = getCountryCodeFromAddress(results[0].address_components);
      callback?.({
        ...pos,
        address: results[0].formatted_address,
        city,
        country,
        displayText,
        countryCode,
      });
    } else {
      callback?.({
        ...pos,
        address: `${pos.lat.toFixed(5)}, ${pos.lng.toFixed(5)}`,
        city: "",
        country: "",
        displayText: `${pos.lat.toFixed(5)}, ${pos.lng.toFixed(5)}`,
        countryCode: null,
      });
    }
  });
}
